"use client";

import type React from "react";

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Download,
  Share2,
  FileText,
  Search,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { pdfjs } from "react-pdf";
// Version-locked worker: react-pdf bundles a specific pdfjs API version, and
// pdf.js refuses to run if the worker version differs. Resolving the worker via
// the installed pdfjs-dist (a direct dep pinned to react-pdf's version) keeps
// them in lockstep — the old static /pdf.worker.min.js had drifted (API 4.8.69
// vs worker 5.3.31) which made every PDF fail with "Failed to load PDF".
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();
import { Document, Page } from "react-pdf";

interface PDFViewerProps {
  url: string;
  title?: string;
  className?: string;
  onDownload?: () => void;
  onShare?: () => void;
}

export function PDFViewer({
  url,
  title = "Document",
  className,
  onDownload,
  onShare,
}: PDFViewerProps) {
  const { toast } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [isBrowser, setIsBrowser] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Mobile detection
  useEffect(() => {
    setIsBrowser(true);

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Reset state on url change. Loading + page count are driven by react-pdf's
  // <Document> (loading/error props + onLoadSuccess/onLoadError) — not a fake
  // timer, which previously showed a bogus "Page 1 of 5" before the real load.
  useEffect(() => {
    if (!url) {
      setError("No document URL provided");
      setIsLoading(false);
      return;
    }
    setError(null);
    setIsLoading(false);
  }, [url]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          handlePreviousPage();
          break;
        case "ArrowRight":
          event.preventDefault();
          handleNextPage();
          break;
        case "=":
        case "+":
          event.preventDefault();
          handleZoomIn();
          break;
        case "-":
          event.preventDefault();
          handleZoomOut();
          break;
        case "r":
        case "R":
          event.preventDefault();
          handleRotate();
          break;
        case "f":
        case "F":
          event.preventDefault();
          toggleFullscreen();
          break;
        case "/":
          event.preventDefault();
          setShowSearch(!showSearch);
          break;
        case "Escape":
          if (showSearch) {
            setShowSearch(false);
            setSearchTerm("");
          }
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showSearch]);

  if (!isBrowser) return null;

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const onDoucmentLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setTotalPages(n);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 25, 50));
  };

  const handleZoomReset = () => {
    setZoomLevel(100);
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handlePageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const page = Number.parseInt(e.target.value);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleShare = async () => {
    if (onShare) {
      onShare();
      return;
    }

    // Default share behavior
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          url: url,
        });
      } catch (error) {
        // User cancelled or error occurred
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(url);
        toast({
          title: "Link copied",
          description: "Document link copied to clipboard.",
        });
      } catch (error) {
        toast({
          title: "Share failed",
          description: "Could not share the document.",
          variant: "destructive",
        });
      }
    }
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      toast({
        title: "Search",
        description: `Searching for "${searchTerm}"...`,
      });
      // Implement search functionality here
    }
  };

  if (error) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center h-full bg-white/5 rounded-2xl p-8",
          className,
        )}
      >
        <FileText className="h-16 w-16 text-red-400 mb-4" />
        <h3 className="text-white font-syne font-semibold text-xl mb-2">
          Document Error
        </h3>
        <p className="text-white/60 text-sm font-inter text-center mb-4">
          {error}
        </p>
        <Button
          onClick={() => window.open(url, "_blank")}
          variant="default"
          className="font-inter"
        >
          Open in New Tab
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col h-full bg-white/5 rounded-2xl overflow-hidden",
        className,
      )}
      ref={containerRef}
    >
      {/* Header Controls */}
      <div className="flex items-center justify-center p-4 border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-[#BFA2DB]" />
          <div>
            <h3 className="text-white font-syne font-semibold text-sm truncate max-w-[200px]">
              {title}
            </h3>
            <p className="text-white/60 text-xs font-inter">
              Page {currentPage} of {totalPages}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Search Toggle */}
          {/* <Button
            size="icon"
            variant="ghost"
            onClick={() => setShowSearch(!showSearch)}
            className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
            title="Search (Press /)"
          >
            <Search className="h-4 w-4" />
          </Button> */}

          {/* Download */}
          {/* <Button
            size="icon"
            variant="ghost"
            onClick={handleDownload}
            className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
            title="Download"
          >
            <Download className="h-4 w-4" />
          </Button> */}

          {/* Share */}
          {/* <Button
            size="icon"
            variant="ghost"
            onClick={handleShare}
            className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
            title="Share"
          >
            <Share2 className="h-4 w-4" />
          </Button> */}
        </div>
      </div>

      {/* Search Bar */}
      {showSearch && (
        <div className="p-3 border-b border-white/10 bg-black/10">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search in document..."
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 font-inter text-sm pr-8"
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                autoFocus
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={handleSearch}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 text-white/60 hover:text-white"
              >
                <Search className="h-3 w-3" />
              </Button>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => {
                setShowSearch(false);
                setSearchTerm("");
              }}
              className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Document Viewer */}
      <div className="flex-1 relative overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="h-8 w-8 border-2 border-[#BFA2DB] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-white font-syne font-medium">
                Loading document...
              </p>
              <p className="text-white/60 text-sm font-inter mt-1">
                Please wait
              </p>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center p-4">
            {/* PDF Placeholder - In real implementation, this would be a PDF renderer */}
            <div
              className="bg-white rounded-lg shadow-2xl transition-all duration-200 max-w-full max-h-full overflow-auto"
              style={{
                transform: `scale(${zoomLevel / 100}) rotate(${rotation}deg)`,
                transformOrigin: "center center",
              }}
            >
              {/* Mock PDF Page */}
              {/* <div className="w-[600px] h-[800px] bg-white p-8 text-gray-800 font-serif leading-relaxed">
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-bold mb-2">{title}</h1>
                  <p className="text-gray-600">Page {currentPage}</p>
                </div>

                <div className="space-y-4">
                  <p>
                    This is a mock PDF viewer showing page {currentPage} of {totalPages}. In a real implementation, this
                    would render the actual PDF content using a library like PDF.js or react-pdf.
                  </p>

                  <p>
                    The viewer includes features like zoom controls, page navigation, rotation, search functionality,
                    and download/share options.
                  </p>

                  <p>
                    Use the controls below to navigate through the document, or use keyboard shortcuts: Arrow keys for
                    navigation, +/- for zoom, R for rotation, F for fullscreen, and / for search.
                  </p>

                  {searchTerm && (
                    <div className="bg-yellow-200 p-2 rounded">
                      <strong>Search term:</strong> "{searchTerm}"
                    </div>
                  )}
                </div>
              </div> */}

              <Document
                file={url}
                onLoadSuccess={onDoucmentLoadSuccess}
                loading={<p>Loading document...</p>}
                error={<p>Failed to load PDF.</p>}
              >
                {/* {Array.from(new Array(totalPages), (_: undefined, index: number) => (
                  <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  width={600}          // or `scale={1.5}` to zoom
                  loading={<p>Loading page…</p>}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  />))} */}
                <Page
                  pageNumber={currentPage}
                  width={600}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      <div className="flex items-center justify-between p-4 border-t border-white/10 bg-black/20 backdrop-blur-sm">
        {/* Page Navigation */}
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            onClick={handlePreviousPage}
            disabled={currentPage <= 1}
            className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="1"
              max={totalPages}
              value={currentPage}
              onChange={handlePageInput}
              className="w-16 h-8 text-center bg-white/10 border-white/20 text-white text-sm"
            />
            <span className="text-white/60 text-sm font-inter">
              of {totalPages}
            </span>
          </div>

          <Button
            size="icon"
            variant="ghost"
            onClick={handleNextPage}
            disabled={currentPage >= totalPages}
            className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-50"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Zoom and Rotation Controls */}
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            onClick={handleZoomOut}
            className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
            title="Zoom Out (-)"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-2">
            <span className="text-white/70 text-sm font-mono min-w-[3rem] text-center">
              {zoomLevel}%
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleZoomReset}
              className="h-6 px-2 text-xs text-white/60 hover:text-white hover:bg-white/10 rounded"
            >
              Reset
            </Button>
          </div>

          <Button
            size="icon"
            variant="ghost"
            onClick={handleZoomIn}
            className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
            title="Zoom In (+)"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>

          <div className="w-px h-4 bg-white/20 mx-1" />

          <Button
            size="icon"
            variant="ghost"
            onClick={handleRotate}
            className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
            title="Rotate (R)"
          >
            <RotateCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Keyboard Shortcuts Help */}
      {!isMobile && (
        <div className="absolute bottom-2 left-2 text-xs text-white/40 font-inter">
          <span>
            ← → Navigate • +/- Zoom • R Rotate • / Search • F Fullscreen
          </span>
        </div>
      )}
    </div>
  );
}
